import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, useCart } from "../App";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Sparkles, Lock } from "lucide-react";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

// Trim `+91`, leading `91`, or leading `0` from phone numbers pasted in from
// address books / autofill. We accept anything with digits, spaces, dashes or
// brackets and boil it down to a clean 10-digit Indian mobile.
function normalizeIndianPhone(raw) {
  if (raw == null) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length <= 10) return digits;
  if (digits.startsWith("91") && digits.length === 12) return digits.slice(2);
  if (digits.startsWith("0") && digits.length === 11) return digits.slice(1);
  // Anything longer than 10 — take the trailing 10 (handles messy paste like "IN +91-...")
  return digits.slice(-10);
}

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = RAZORPAY_SCRIPT;
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const { user } = useAuth();
  const { cart, refreshCart, clearGuestCart } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [address, setAddress] = useState({
    full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "",
  });
  const [payment, setPayment] = useState("razorpay");
  const isGuest = !user;

  // If guest cart empty & signed-out, bounce to cart page.
  useEffect(() => {
    if (cart.items && cart.items.length === 0) {
      // Small delay so refreshCart on mount has a chance to populate
      const t = setTimeout(() => {
        if ((cart.items || []).length === 0) navigate("/cart");
      }, 250);
      return () => clearTimeout(t);
    }
  }, [cart, navigate]);
  useEffect(() => { loadRazorpay(); }, []);

  // Pre-fill contact + address name for logged-in users (from Google sign-in),
  // and hydrate their whole shipping address from the most recent order so they
  // don't need to retype it every time.
  const [prefilledFromHistory, setPrefilledFromHistory] = useState(false);
  useEffect(() => {
    if (!user) return;
    setContact(c => ({ name: c.name || user.name || "", email: c.email || user.email || "", phone: c.phone }));
    setAddress(a => ({ ...a, full_name: a.full_name || user.name || "" }));
    axios.get(`${API}/user/last-address`)
      .then(r => {
        const saved = r.data?.address;
        if (!saved) return;
        // Only fill fields the customer hasn't already touched, so re-typing wins.
        setAddress(a => ({
          full_name: a.full_name || saved.full_name || user.name || "",
          phone:     a.phone     || normalizeIndianPhone(saved.phone),
          line1:     a.line1     || saved.line1 || "",
          line2:     a.line2     || saved.line2 || "",
          city:      a.city      || saved.city  || "",
          state:     a.state     || saved.state || "",
          pincode:   a.pincode   || saved.pincode || "",
        }));
        setContact(c => ({ ...c, phone: c.phone || normalizeIndianPhone(saved.phone) }));
        setPrefilledFromHistory(true);
      })
      .catch(() => { /* silently ignore — new customers or 401 */ });
  }, [user]);

  const clearSavedAddress = () => {
    setAddress({ full_name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });
    setPrefilledFromHistory(false);
  };

  // Autofill City/State from pincode via India Post
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const lookupPincode = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    setPinLookupLoading(true);
    try {
      const r = await axios.get(`${API}/shipping/pincode/${pin}`);
      setAddress(a => ({ ...a, city: r.data.city || a.city, state: r.data.state || a.state }));
    } catch { /* silent — user can still type manually */ }
    finally { setPinLookupLoading(false); }
  };

  // Live shipping calc based on payment method + pincode
  const [shipInfo, setShipInfo] = useState({ shipping: 0, etd: null });
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!/^\d{6}$/.test(address.pincode)) { setShipInfo({ shipping: 0, etd: null }); return; }
      if (payment !== "cod") { setShipInfo({ shipping: 0, etd: null }); return; }
      try {
        const weight = Math.max(0.1, (cart.items || []).reduce((s, it) => s + (Number(it.product?.weight) || 0) * it.quantity, 0) / 1000);
        const r = await axios.get(`${API}/shipping/serviceability`, { params: { pincode: address.pincode, weight, cod: 1 } });
        if (!ignore) {
          setShipInfo(r.data.serviceable
            ? { shipping: r.data.total, etd: r.data.etd, freight: r.data.freight_charges, cod: r.data.cod_charges }
            : { shipping: 0, etd: null, error: "Pincode not serviceable" });
        }
      } catch {
        if (!ignore) setShipInfo({ shipping: 0, etd: null });
      }
    })();
    return () => { ignore = true; };
  }, [address.pincode, payment, cart.items]);

  const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const shipping = shipInfo.shipping || 0;
  const total = cart.total + shipping;
  const codBlocked = total > 50000;

  // Auto-switch away from COD if the total crosses the threshold
  useEffect(() => {
    if (codBlocked && payment === "cod") setPayment("razorpay");
  }, [codBlocked, payment]);

  const placeOrder = async (e) => {
    e.preventDefault();
    const required = ["full_name", "phone", "line1", "city", "state", "pincode"];
    if (required.some(k => !address[k])) {
      toast.error("Please fill all required address fields");
      return;
    }
    if (!/^\d{10}$/.test(address.phone)) {
      toast.error("Please enter a valid 10-digit phone");
      return;
    }
    if (!/^\d{6}$/.test(address.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }
    if (isGuest) {
      if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) {
        toast.error("Please share your name, email and phone so we can send your receipt");
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(contact.email)) {
        toast.error("Please enter a valid email");
        return;
      }
    }

    setPlacing(true);
    try {
      // Choose endpoint & payload based on auth state
      const endpoint = isGuest ? `${API}/orders/guest` : `${API}/orders`;
      const payload = isGuest
        ? {
            items: (cart.items || []).map(it => ({ product_id: it.product_id, quantity: it.quantity, size: it.size || null })),
            contact: { name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim() },
            address,
            payment_method: payment,
          }
        : { address, payment_method: payment };
      const r = await axios.post(endpoint, payload);
      const order = r.data;

      if (payment === "cod") {
        if (isGuest) clearGuestCart(); else await refreshCart();
        toast.success("Order placed successfully");
        navigate(`/order-success/${order.order_id}${isGuest ? `?email=${encodeURIComponent(contact.email.trim())}` : ""}`);
        return;
      }

      // Razorpay flow
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Razorpay SDK failed to load. Check your connection.");
        setPlacing(false);
        return;
      }

      const options = {
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpay_order_id,
        name: "Sumanya",
        description: `Order ${order.order_id}`,
        image: "https://customer-assets.emergentagent.com/job_sumanya-collection/artifacts/v848dqa8_Sumanya_logo_hd-removebg-preview.png",
        prefill: {
          name: order.user_name || user?.name || contact.name,
          email: order.user_email || user?.email || contact.email,
          contact: address.phone,
        },
        notes: { sumanya_order_id: order.order_id },
        theme: { color: "#8B1E28" },
        handler: async (res) => {
          try {
            const verifyUrl = isGuest ? `${API}/orders/guest/verify-payment` : `${API}/orders/verify-payment`;
            await axios.post(verifyUrl, {
              order_id: order.order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
            if (isGuest) clearGuestCart(); else await refreshCart();
            toast.success("Payment successful");
            navigate(`/order-success/${order.order_id}${isGuest ? `?email=${encodeURIComponent(contact.email.trim())}` : ""}`);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled. You can retry from your cart.");
            setPlacing(false);
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Try again"}`);
        setPlacing(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="checkout-page">
      <h1 className="font-display text-4xl text-[#2C2A29] mb-2">Checkout</h1>
      {isGuest && (
        <p className="text-sm text-[#8A8480] mb-8" data-testid="guest-checkout-note">
          Checking out as a guest — your order receipt will be emailed to you. Have an account?{" "}
          <button type="button" onClick={() => window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.href)}`}
            className="text-[#8B1E28] underline hover:no-underline" data-testid="guest-signin-link">
            Sign in for faster checkout
          </button>
        </p>
      )}

      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-8">
          {isGuest && (
            <section className="bg-white border border-[#EAE3D9] rounded-2xl p-6" data-testid="contact-section">
              <h2 className="font-display text-2xl text-[#2C2A29] mb-1">Your contact</h2>
              <p className="text-xs text-[#8A8480] mb-5">So we can email you the receipt and delivery updates.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Full name *</Label>
                  <Input value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} required data-testid="contact-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} required placeholder="you@example.com" data-testid="contact-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={contact.phone} onChange={e => setContact({ ...contact, phone: normalizeIndianPhone(e.target.value) })} required placeholder="10-digit mobile" data-testid="contact-phone" />
                </div>
              </div>
            </section>
          )}

          {/* Shipping */}
          <section className="bg-white border border-[#EAE3D9] rounded-2xl p-6" data-testid="shipping-section">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <h2 className="font-display text-2xl text-[#2C2A29]">Shipping address</h2>
              {prefilledFromHistory && (
                <div className="text-xs text-[#8A8480] flex items-center gap-2" data-testid="address-prefilled-hint">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                    <Sparkles className="h-3 w-3" /> Prefilled from your last order
                  </span>
                  <button type="button" onClick={clearSavedAddress} className="text-[#8B1E28] hover:underline" data-testid="address-clear-saved">
                    Use a different address
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Full name *</Label>
                <Input value={address.full_name} onChange={e => setAddress({ ...address, full_name: e.target.value })} required data-testid="address-name" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={address.phone} onChange={e => setAddress({ ...address, phone: normalizeIndianPhone(e.target.value) })} required placeholder="10-digit mobile" data-testid="address-phone" />
              </div>
              <div>
                <Label>Pincode *</Label>
                <Input
                  value={address.pincode}
                  onChange={e => {
                    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setAddress(a => ({ ...a, pincode: pin }));
                    if (pin.length === 6) lookupPincode(pin);
                  }}
                  onBlur={e => lookupPincode(e.target.value)}
                  required placeholder="6-digit"
                  data-testid="address-pincode"
                />
                {pinLookupLoading && <div className="text-[11px] text-[#8A8480] mt-1">Looking up city…</div>}
              </div>
              <div className="sm:col-span-2">
                <Label>Address line 1 *</Label>
                <Input value={address.line1} onChange={e => setAddress({ ...address, line1: e.target.value })} required data-testid="address-line1" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address line 2</Label>
                <Input value={address.line2} onChange={e => setAddress({ ...address, line2: e.target.value })} data-testid="address-line2" />
              </div>
              <div>
                <Label>City *</Label>
                <Input value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} required data-testid="address-city" />
              </div>
              <div>
                <Label>State *</Label>
                <Input value={address.state} onChange={e => setAddress({ ...address, state: e.target.value })} required data-testid="address-state" />
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white border border-[#EAE3D9] rounded-2xl p-6" data-testid="payment-section">
            <h2 className="font-display text-2xl text-[#2C2A29] mb-2">Payment</h2>
            <div className="flex items-center gap-2 mb-5 text-xs text-[#8A8480]">
              <Sparkles className="h-3.5 w-3.5 text-[#C5A059]" /> <span>Secure payments by Razorpay · 256-bit SSL · BIS protected</span>
            </div>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
              <label className="flex items-center gap-3 p-4 border border-[#EAE3D9] rounded-xl cursor-pointer hover:bg-[#FFF5F3]/60">
                <RadioGroupItem value="razorpay" data-testid="pay-razorpay" />
                <div>
                  <div className="font-medium">Razorpay (UPI, Cards, Netbanking, Wallets)</div>
                  <div className="text-xs text-[#8A8480]">Recommended · All payment modes</div>
                </div>
              </label>
              <label
                className={`flex items-center gap-3 p-4 border rounded-xl transition-colors ${
                  codBlocked
                    ? "border-[#EAE3D9] bg-[#FDFBF7] opacity-60 cursor-not-allowed"
                    : "border-[#EAE3D9] cursor-pointer hover:bg-[#FFF5F3]/60"
                }`}
                data-testid="cod-option"
              >
                <RadioGroupItem value="cod" data-testid="pay-cod" disabled={codBlocked} />
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    Cash on Delivery
                    {codBlocked && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#8B1E28]/10 text-[#8B1E28]"
                        data-testid="cod-blocked-badge"
                      >
                        Unavailable
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#8A8480]">
                    {codBlocked
                      ? "Not available for orders above ₹50,000 — please pay via Razorpay."
                      : "For orders below ₹50,000"}
                  </div>
                </div>
              </label>
            </RadioGroup>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-[#EAE3D9] rounded-2xl p-6 h-fit sticky top-32">
          <h3 className="font-display text-2xl text-[#2C2A29] mb-4">Order summary</h3>
          <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
            {cart.items.map(it => (
              <div key={it.product_id + (it.size || "")} className="flex gap-3">
                <img src={it.product?.images?.[0]} alt="" className="h-14 w-14 rounded-lg object-cover bg-[#FFF5F3]" />
                <div className="flex-1 text-sm">
                  <div className="line-clamp-1">{it.product?.name}</div>
                  <div className="text-xs text-[#8A8480]">Qty {it.quantity} {it.size ? `· ${it.size}` : ""}</div>
                </div>
                <div className="text-sm font-semibold">{inr(it.product?.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm pt-4 border-t border-[#EAE3D9]">
            <div className="flex justify-between"><span className="text-[#595552]">Subtotal</span><span>{inr(cart.total)}</span></div>
            <div className="flex justify-between">
              <span className="text-[#595552]">
                Shipping
                {payment === "cod" && shipInfo.etd && (
                  <span className="block text-[11px] text-[#8A8480] mt-0.5">
                    Courier {inr(shipInfo.freight || 0)}{(shipInfo.cod || 0) > 0 ? ` + COD ${inr(shipInfo.cod)}` : ""}
                  </span>
                )}
              </span>
              <span data-testid="checkout-shipping">
                {payment !== "cod" ? "Free" : (shipping === 0 ? (address.pincode?.length === 6 ? "—" : "Enter pincode") : inr(shipping))}
              </span>
            </div>
            {shipInfo.etd && (
              <div className="flex justify-between text-[11px] text-[#8A8480]"><span>Est. delivery</span><span data-testid="checkout-etd">{shipInfo.etd}</span></div>
            )}
            <div className="flex justify-between pt-3 border-t border-[#EAE3D9] mt-3 font-semibold">
              <span>Total</span><span className="text-lg" data-testid="checkout-total">{inr(total)}</span>
            </div>
          </div>
          <Button type="submit" disabled={placing}
            className="btn-primary w-full mt-6 rounded-full py-6 h-auto" data-testid="place-order-btn">
            <Lock className="h-4 w-4 mr-2" /> {placing ? "Processing…" : `Pay ${inr(total)}`}
          </Button>
          <p className="text-xs text-[#8A8480] mt-3 text-center">By placing this order you agree to our terms</p>
        </aside>
      </form>
    </div>
  );
}
